import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { login, LoginFailed, useSession } from '../lib/auth.ts';
import { forgotUrl, safeReturn } from '../lib/safeReturn.ts';
import { T } from '../lib/strings.ts';
import { Input, Label } from '../components/ui/input.tsx';
import { Button } from '../components/ui/button.tsx';
import { Logo } from '../components/brand/Logo.tsx';
import { IconAlert } from '../components/brand/icons.tsx';
import { CompanyLogo } from '../components/brand/CompanyLogo.tsx';
import { useBrand, useDocTitle } from '../lib/brand.ts';

// web-login-stage: הבמה ("היכולות המרכזיות", framer-motion) נטענת עצלה, רק ב-≥ 861px. במובייל לא יורד ממנה כלום.
const Capabilities = lazy(() => import('../components/stage/Capabilities.tsx'));
const STAGE = '(min-width: 861px)';
function useWide() {
  const [wide, setWide] = useState(() => window.matchMedia(STAGE).matches);
  useEffect(() => {
    const m = window.matchMedia(STAGE);
    const on = () => setWide(m.matches);
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, []);
  return wide;
}

export function LoginPage() {
  const [qs] = useSearchParams();
  const target = safeReturn(qs.get('return'));
  // 26 §1.2: קישור כניסה שפג/שימש — הודעה קבועה (בלי לרמוז אם המשתמש קיים), ובלי מעבר אוטומטי גם אם יש כניסה אחרת בדפדפן
  const linkExpired = qs.get('reason') === 'link_expired';
  // web-session-across-windows: הסשן פקע בשרת — הסבר במקום "למה זרקו אותי"; כניסה (גם בלשונית אחרת) ממשיכה כרגיל
  const sessionExpired = qs.get('reason') === 'session_expired';
  const nav = useNavigate();
  const s = useSession();
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const pwRef = useRef<HTMLInputElement>(null);
  const wide = useWide();
  // web-instance-identity: מתחת ללוגו aibify — לוגו החברה בלבד באריח מרובע 96 (השם ב-title ובכותרת הלשונית), קו מפריד, ואז הכותרת
  const brand = useBrand();
  useDocTitle(T.login.page);

  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    if (s && (!linkExpired || dismissed)) nav(target, { replace: true });
  }, [s, nav, target, linkExpired, dismissed]);

  return (
    <main className="grid min-h-(--ab-screen) grid-cols-1 bg-background text-foreground min-[861px]:grid-cols-[minmax(420px,520px)_minmax(0,1fr)]" data-testid="login-page">
      {/* design §2: טור הטופס (בצד ה-start, מימין), משטח לבן; לוגו מלא 72px (56 במובייל) עם הנפשת הצמתים */}
      <section className="flex flex-col justify-center gap-7 bg-card px-4 py-8 [--logo-gap:var(--card)] min-[861px]:border-e min-[861px]:border-border min-[861px]:px-14 min-[861px]:py-12">
        <div className="flex w-full flex-col gap-7">
          <Logo className="h-14 self-start min-[861px]:h-[72px]" active />
          {brand?.nameHe && (
            <div className="border-b border-line pb-5" data-testid="login-company">
              <CompanyLogo brand={brand} size={96} />
            </div>
          )}
          <div className="flex flex-col gap-2.5">
            <h1 className="m-0 text-[26px] leading-tight font-bold text-balance min-[861px]:text-[28px]">
              {T.login.headline[0]}
              <br />
              {T.login.headline[1]}
            </h1>
            <p className="m-0 text-[15px] text-muted-foreground text-pretty">{T.login.lead}</p>
          </div>
          {linkExpired && (
            <p role="alert" className="m-0 flex items-center gap-2 rounded-sm border border-degraded/50 bg-degraded/[0.08] px-3 py-2 text-sm font-medium" data-testid="link-expired">
              <IconAlert size={16} />
              {T.login.linkExpired}
            </p>
          )}
          {sessionExpired && (
            <p role="status" className="m-0 flex items-center gap-2 rounded-sm border border-degraded/50 bg-degraded/[0.08] px-3 py-2 text-sm font-medium" data-testid="session-expired">
              <IconAlert size={16} />
              {T.login.sessionExpired}
            </p>
          )}
          <form
            className="flex flex-col gap-3.5"
            aria-label={T.login.title}
            noValidate
            onSubmit={async (e) => {
              e.preventDefault();
              setErr('');
              if (!email.trim() || !pw) return setErr(T.login.missing);
              setBusy(true);
              try {
                await login(email.trim(), pw);
                setDismissed(true);
                // הניווט ב-useEffect, אל target המסונן בלבד
              } catch (x) {
                setErr(x instanceof LoginFailed ? x.textHe : T.network);
                pwRef.current?.select();
              } finally {
                setBusy(false);
              }
            }}
          >
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="email">{T.login.email}</Label>
              <Input id="email" type="email" dir="ltr" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="password">{T.login.password}</Label>
              <Input id="password" ref={pwRef} type="password" dir="ltr" autoComplete="current-password" value={pw} onChange={(e) => setPw(e.target.value)} />
            </div>
            {err && (
              <p role="alert" className="m-0 flex items-center gap-2 rounded-sm border border-error/35 bg-error/[0.07] px-3 py-2 text-sm font-medium text-error" data-testid="login-error">
                <IconAlert size={16} />
                {err}
              </p>
            )}
            <Button type="submit" size="lg" className="mt-1 w-full" disabled={busy}>
              {busy ? T.login.busy : T.login.submit}
            </Button>
            {/* platform-password-recovery: שחזור בשירות עצמי; return נשמר רק כשהוא עובר את המגן */}
            <Link to={forgotUrl(qs.get('return'))} className="self-start text-[13px] text-link hover:underline" data-testid="forgot-link">
              {T.login.forgot}
            </Link>
          </form>
        </div>
      </section>

      {/* ≥ 861px: "היכולות המרכזיות" מהאתר (design §1–§3). במובייל רק הטופס */}
      {wide && (
        <Suspense fallback={<div className="bg-background" />}>
          <Capabilities />
        </Suspense>
      )}
    </main>
  );
}
